import { createSlice } from "@reduxjs/toolkit";
import { receiveProducts } from "../search/domains/store/actionCreators";
import { LOCATION_CHANGE } from "../router/routerAdapter";
import { computePriceBounds, PRICE_STEP } from "./model/priceBounds";

// Applied selection is URL-derived; this slice only stores transient or API state.
export const initialState = {
  draft: null,
  catalog: [],
  knownFacetValues: {},
  priceBounds: { min: 0, max: 0, step: PRICE_STEP },
};

const normalizeId = (id) => String(id);

/**
 * A response can omit a value the shopper still has selected, which would leave
 * us unable to label the chip that removes it. Every value we have seen is
 * remembered so the selection can always be rendered and undone.
 */
const rememberFacetValues = (knownFacetValues, facets) => {
  facets.forEach(({ id: facetId, facetValues }) => {
    if (facetId === undefined) {
      return;
    }

    const facetKey = normalizeId(facetId);

    knownFacetValues[facetKey] = knownFacetValues[facetKey] || {};

    (facetValues || []).forEach((facetValue) => {
      if (facetValue?.id !== undefined) {
        knownFacetValues[facetKey][normalizeId(facetValue.id)] = {
          ...facetValue,
          count: 0,
        };
      }
    });
  });
};

/**
 * Mirrors the search results reducer: when a refinement filters every product
 * out, the previous catalog is kept so the shopper can still undo it.
 */
const filteredEverythingOut = (state, payload) =>
  !payload?.isNewPage && payload?.itemCount === 0 && state.catalog.length > 0;

const facetsSlice = createSlice({
  name: "facets",
  initialState,
  reducers: {
    draftChanged: {
      reducer: (state, { payload }) => {
        state.draft = payload;
      },
      prepare: (selection, meta) => ({ payload: selection, meta }),
    },
    draftDiscarded: (state) => {
      state.draft = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(receiveProducts, (state, { error, payload }) => {
        if (error) {
          return;
        }

        const facets = Array.isArray(payload?.facets) ? payload.facets : [];

        rememberFacetValues(state.knownFacetValues, facets);

        if (filteredEverythingOut(state, payload)) {
          return;
        }

        state.catalog = facets;
        state.priceBounds = computePriceBounds({
          facets,
          query: payload?.query || {},
        });
      })
      .addCase(LOCATION_CHANGE, (state) => {
        state.draft = null;
      });
  },
});

export const { draftChanged, draftDiscarded } = facetsSlice.actions;

export default facetsSlice.reducer;
